import { Box, BoxProps, Image } from '@chakra-ui/react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import * as React from 'react';

type StaticImageFile = {
  relativePath: string;
  publicURL?: string | null;
  childImageSharp?: {
    gatsbyImageData: IGatsbyImageData;
  } | null;
};

type StaticImagesQuery = {
  photos: {
    nodes: StaticImageFile[];
  };
  energyLabels: {
    nodes: StaticImageFile[];
  };
};

export type StaticAssetImageProps = Omit<BoxProps, 'children'> & {
  src?: string | null;
  alt: string;
  loading?: 'eager' | 'lazy';
  objectFit?: React.CSSProperties['objectFit'];
  objectPosition?: React.CSSProperties['objectPosition'];
};

const normalizeStaticPath = (src?: string | null) => src?.replace(/^\/+/, '');

const useStaticImages = () =>
  useStaticQuery<StaticImagesQuery>(graphql`
    query OptimizedStaticAssetImages {
      photos: allFile(
        filter: {
          sourceInstanceName: { eq: "static-images" }
          extension: { regex: "/(jpg|jpeg|png|webp)/" }
          relativeDirectory: { ne: "envkv" }
        }
      ) {
        nodes {
          relativePath
          publicURL
          childImageSharp {
            gatsbyImageData(
              layout: FULL_WIDTH
              breakpoints: [320, 480, 768, 1024, 1440, 1920]
              quality: 78
              formats: [AUTO, WEBP]
              placeholder: BLURRED
            )
          }
        }
      }
      energyLabels: allFile(
        filter: {
          sourceInstanceName: { eq: "static-images" }
          extension: { eq: "png" }
          relativeDirectory: { eq: "envkv" }
        }
      ) {
        nodes {
          relativePath
          publicURL
          childImageSharp {
            gatsbyImageData(
              layout: CONSTRAINED
              breakpoints: [160, 240, 320, 480, 768, 1024, 1392]
              quality: 90
              formats: [AUTO, WEBP]
            )
          }
        }
      }
    }
  `);

const findStaticImage = (data: StaticImagesQuery, src?: string | null) => {
  const relativePath = normalizeStaticPath(src);
  if (!relativePath) return undefined;
  return [...data.photos.nodes, ...data.energyLabels.nodes].find(item => item.relativePath === relativePath);
};

export const useStaticAssetImageData = (src: string) =>
  findStaticImage(useStaticImages(), src)?.childImageSharp?.gatsbyImageData;

/**
 * Serves raster files from /static through Gatsby's responsive image pipeline.
 * Public URLs remain available for metadata and other non-visual consumers.
 */
export const StaticAssetImage: React.FC<StaticAssetImageProps> = ({
  src,
  alt,
  loading = 'lazy',
  objectFit = 'cover',
  objectPosition = 'center center',
  ...boxProps
}) => {
  const file = findStaticImage(useStaticImages(), src);
  const imageData = file?.childImageSharp?.gatsbyImageData;
  const preserveIntrinsicRatio = objectFit === 'contain';

  if (imageData) {
    return (
      <Box
        w="full"
        h={preserveIntrinsicRatio ? 'auto' : 'full'}
        overflow={preserveIntrinsicRatio ? 'visible' : 'hidden'}
        {...boxProps}
      >
        <GatsbyImage
          image={imageData}
          alt={alt}
          loading={loading}
          objectFit={objectFit}
          objectPosition={objectPosition}
          style={{ width: '100%', height: preserveIntrinsicRatio ? 'auto' : '100%' }}
        />
      </Box>
    );
  }

  const fallbackSrc = file?.publicURL ?? src;

  if (!fallbackSrc) {
    return null;
  }

  return (
    <Image
      src={fallbackSrc}
      alt={alt}
      loading={loading}
      objectFit={objectFit}
      objectPosition={objectPosition}
      w="full"
      h={preserveIntrinsicRatio ? 'auto' : 'full'}
      {...boxProps}
    />
  );
};
